// src/components/ODFScoreCard.jsx
// Full ODF Score visual breakdown component
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, Info, TrendingUp } from 'lucide-react';

export default function ODFScoreCard({ score: s, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  if (!s) return null;

  const dimOrder = ['profitability','consistency','riskControl','execution','discipline','sampleSize'];
  const dimLabels = {
    profitability: 'Profitability',
    consistency:   'Consistency',
    riskControl:   'Risk Control',
    execution:     'Execution',
    discipline:    'Discipline',
    sampleSize:    'Sample Size',
  };

  if (compact) {
    return (
      <div style={{ background:'#16161f', border:`1px solid ${s.color}25`, borderRadius:12, padding:'16px 18px', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:s.color }} />
        <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>ODF Score</div>
        <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:4 }}>
          <div style={{ fontSize:32, fontWeight:700, color:s.color, fontFamily:"'DM Mono',monospace", letterSpacing:'-0.02em' }}>{s.total}</div>
          <div style={{ fontSize:18, fontWeight:700, color:s.color }}>{s.grade}</div>
        </div>
        <div style={{ fontSize:12, color:s.color, fontFamily:"'IBM Plex Sans',monospace", marginBottom:8 }}>{s.label}</div>
        {/* Mini dimension bars */}
        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          {dimOrder.filter(k => k !== 'sampleSize').map(key => (
            <div key={key} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ fontSize:9, color:'#55556a', fontFamily:"'DM Mono',monospace", width:60, flexShrink:0 }}>{dimLabels[key].slice(0,8)}</div>
              <div style={{ flex:1, background:'#1e1e2e', borderRadius:3, height:4, overflow:'hidden' }}>
                <div style={{ width:`${s.dims[key].score}%`, height:'100%', background: s.dims[key].score >= 70 ? '#00e5a0' : s.dims[key].score >= 45 ? '#ffd166' : '#ff4d6d', borderRadius:3, transition:'width 0.6s ease' }} />
              </div>
              <div style={{ fontSize:9, color:'#55556a', fontFamily:"'DM Mono',monospace", width:24, textAlign:'right' }}>{s.dims[key].score}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid #1e1e2e', display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:s.credibility.color, flexShrink:0 }} />
          <div style={{ fontSize:10, color:s.credibility.color, fontFamily:"'DM Mono',monospace" }}>{s.credibility.label}</div>
          <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>· {s.tradeCount} trades</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background:'#16161f', border:`1px solid ${s.color}20`, borderRadius:14, overflow:'hidden', marginBottom:16 }}>
      {/* Top accent bar */}
      <div style={{ height:3, background:`linear-gradient(90deg, ${s.color}, ${s.color}44)` }} />

      {/* Header */}
      <div style={{ padding:'20px 24px', display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
        {/* Big score */}
        <div style={{ display:'flex', alignItems:'baseline', gap:10, flexShrink:0 }}>
          <div style={{ fontSize:52, fontWeight:700, color:s.color, fontFamily:"'DM Mono',monospace", letterSpacing:'-0.03em', lineHeight:1 }}>{s.total}</div>
          <div>
            <div style={{ fontSize:24, fontWeight:700, color:s.color, lineHeight:1 }}>{s.grade}</div>
            <div style={{ fontSize:12, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>/100</div>
          </div>
        </div>

        {/* Label + desc */}
        <div style={{ flex:1 }}>
          <div style={{ fontSize:18, fontWeight:600, color:'#f0f0f5', marginBottom:4, fontFamily:"'IBM Plex Sans',sans-serif" }}>{s.label}</div>
          <div style={{ fontSize:13, color:'#8888a8', lineHeight:1.6 }}>{s.desc}</div>
        </div>

        {/* Credibility badge */}
        <div style={{ background:`${s.credibility.color}10`, border:`1px solid ${s.credibility.color}30`, borderRadius:10, padding:'10px 16px', textAlign:'center', flexShrink:0 }}>
          <div style={{ fontSize:18, marginBottom:2 }}>{s.credibility.icon}</div>
          <div style={{ fontSize:11, fontWeight:600, color:s.credibility.color, fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.06em' }}>{s.credibility.label}</div>
          <div style={{ fontSize:10, color:'#55556a', marginTop:2 }}>{s.tradeCount} trades</div>
        </div>

        <button onClick={() => setExpanded(e => !e)} style={{ background:'transparent', border:'1px solid #1e1e2e', borderRadius:8, padding:'8px 12px', cursor:'pointer', color:'#8888a8', display:'flex', alignItems:'center', gap:5, fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s', flexShrink:0 }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor='#2a2a3e';e.currentTarget.style.color='#f0f0f5';}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor='#1e1e2e';e.currentTarget.style.color='#8888a8';}}>
          {expanded ? <><ChevronUp size={13}/> Hide</> : <><ChevronDown size={13}/> Details</>}
        </button>
      </div>

      {/* Dimension bars — always visible */}
      <div style={{ padding:'0 24px 16px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:10 }}>
          {dimOrder.map(key => {
            const dim = s.dims[key];
            const barColor = dim.score >= 70 ? '#00e5a0' : dim.score >= 45 ? '#ffd166' : '#ff4d6d';
            return (
              <div key={key} style={{ background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:10, padding:'12px 14px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <div style={{ fontSize:11, color:'#8888a8', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.06em' }}>{dim.label}</div>
                  <div style={{ fontSize:14, fontWeight:700, color:barColor, fontFamily:"'DM Mono',monospace" }}>{dim.score}</div>
                </div>
                <div style={{ background:'#1e1e2e', borderRadius:4, height:5, overflow:'hidden', marginBottom:6 }}>
                  <div style={{ width:`${dim.score}%`, height:'100%', background:barColor, borderRadius:4, transition:'width 0.8s ease' }} />
                </div>
                <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>{dim.detail}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Expanded — Tips + Anomalies */}
      {expanded && (
        <div style={{ borderTop:'1px solid #1e1e2e', padding:'20px 24px' }}>
          {/* Tips */}
          {s.tips.filter(t => t.type === 'improvement').length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>
                <TrendingUp size={11} style={{ verticalAlign:'middle', marginRight:5 }} />
                How to improve your score
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {s.tips.filter(t => t.type === 'improvement').map((tip, i) => (
                  <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', background:'rgba(0,229,160,0.04)', border:'1px solid rgba(0,229,160,0.1)', borderRadius:8, padding:'10px 14px' }}>
                    <div style={{ width:20, height:20, borderRadius:'50%', background:'rgba(0,229,160,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
                      <span style={{ fontSize:9, color:'#00e5a0', fontWeight:700 }}>{i+1}</span>
                    </div>
                    <div>
                      <div style={{ fontSize:10, color:'#00e5a0', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>{tip.dim}</div>
                      <div style={{ fontSize:12, color:'#8888a8', lineHeight:1.6 }}>{tip.msg}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Anomaly flags */}
          {s.anomalies.length > 0 && (
            <div>
              <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>
                <AlertTriangle size={11} style={{ verticalAlign:'middle', marginRight:5 }} />
                Data notes
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {s.anomalies.map((a, i) => (
                  <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', background: a.type==='warning'?'rgba(255,77,109,0.05)':'rgba(76,201,240,0.05)', border:`1px solid ${a.type==='warning'?'rgba(255,77,109,0.15)':'rgba(76,201,240,0.15)'}`, borderRadius:8, padding:'10px 14px' }}>
                    {a.type === 'warning' ? <AlertTriangle size={14} style={{ color:'#ff4d6d', flexShrink:0, marginTop:1 }} /> : <Info size={14} style={{ color:'#4cc9f0', flexShrink:0, marginTop:1 }} />}
                    <div style={{ fontSize:12, color:'#8888a8', lineHeight:1.6 }}>{a.msg}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Credibility explanation */}
          <div style={{ marginTop:16, background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:10, padding:'14px 16px' }}>
            <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
              <div style={{ fontSize:16 }}>{s.credibility.icon}</div>
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:s.credibility.color, marginBottom:3 }}>{s.credibility.label}</div>
                <div style={{ fontSize:12, color:'#55556a', lineHeight:1.6 }}>{s.credibility.desc}</div>
                <div style={{ fontSize:11, color:'#2a2a3e', marginTop:6, fontFamily:"'DM Mono',monospace" }}>
                  Self-reported data · Statistically analysed · Broker sync coming soon
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
